import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useBranchOptions, useDepartmentOptions } from '../hooks/useOrganizationOptions';
import Combobox from '../components/Combobox';
import './Login.css';

const SignUp: React.FC = () => {
  const [formData, setFormData] = useState({
    empNo: '',
    fullName: '',
    department: '',
    branch: '',
    email: '',
    password: '',
    confirmPassword: '',
    telNo: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const branchOptions = useBranchOptions();
  const departmentOptions = useDepartmentOptions();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      await authService.register({
        empNo: formData.empNo,
        fullName: formData.fullName,
        department: formData.department,
        branch: formData.branch,
        email: formData.email,
        password: formData.password,
        telNo: formData.telNo || undefined,
      });

      // Registration successful - redirect to login
      alert('Registration successful! Please sign in.');
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container signup-container">
        <div className="login-header">
          <img src="/toyotsu-lanka-logo.svg" alt="Toyotsu Lanka" className="logo" />
          <h1>Toyotsu Lanka</h1>
          <p className="subtitle">Employee Intranet Portal</p>
        </div>

        <div className="login-form-container">
          <h2>Create Account</h2>
          <p className="form-description">Register to access the portal</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="empNo">Employee No *</label>
                <input
                  type="text"
                  id="empNo"
                  name="empNo"
                  value={formData.empNo}
                  onChange={handleChange}
                  placeholder="E001"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="department">Department *</label>
                <Combobox
                  id="department"
                  ariaLabel="Department"
                  value={formData.department}
                  onChange={(v) => setFormData({ ...formData, department: v })}
                  required
                  placeholder="Select Department"
                  options={departmentOptions.map((d) => ({ value: d, label: d }))}
                />
              </div>

              <div className="form-group">
                <label htmlFor="branch">Branch *</label>
                <Combobox
                  id="branch"
                  ariaLabel="Branch"
                  value={formData.branch}
                  onChange={(v) => setFormData({ ...formData, branch: v })}
                  required
                  placeholder="Select Branch"
                  options={branchOptions.map((b) => ({ value: b, label: b }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@toyota.lk"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="telNo">Phone Number</label>
              <input
                type="tel"
                id="telNo"
                name="telNo"
                value={formData.telNo}
                onChange={handleChange}
                placeholder="077-1234567"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min 6 characters"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating Account...' : 'Sign Up'}
            </button>

            <div className="form-footer">
              <p>Already have an account? <Link to="/login">Sign In</Link></p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
